import purify from 'dompurify'

// These must match what survey-platform's Quill toolbar allows (see its
// src/constants/quillModules.ts) since that's the producer contract we render.
const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'u', 'span', 'ol', 'ul', 'li']
const ALLOWED_ATTR = ['style', 'class']

// ALLOWED_ATTR only controls whether a `style` attribute is kept, not what CSS
// is inside it. Quill's color/background toolbar buttons only ever emit
// `color`/`background-color` declarations, so allowlist exactly those two
// properties (with a color-shaped value) rather than trying to blacklist
// dangerous patterns like `javascript:`/`expression()`, which are easy to
// obfuscate.
const ALLOWED_STYLE_PROPERTIES = new Set(['color', 'background-color'])
const SAFE_STYLE_VALUE =
  /^(#[0-9a-f]{3,8}|[a-z]+|rgba?\([\d.,\s%]+\)|hsla?\([\d.,\s%]+\))$/i

let styleHookRegistered = false

function ensureStyleHookRegistered(): void {
  if (styleHookRegistered) {
    return
  }
  styleHookRegistered = true

  purify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') {
      return
    }

    const declarations = data.attrValue
      .split(';')
      .map((declaration) => declaration.trim())
      .filter((declaration) => {
        const [property, value] = declaration
          .split(':')
          .map((part) => part.trim())

        return (
          !!property &&
          !!value &&
          ALLOWED_STYLE_PROPERTIES.has(property.toLowerCase()) &&
          SAFE_STYLE_VALUE.test(value)
        )
      })

    // Match the browser's own CSSOM serialization (a trailing `;` after every
    // declaration, including the last). A controlled rich-text editor reads
    // style attributes back via `element.style.cssText`, which always has this
    // trailing semicolon; if we omit it here, our sanitized output could never
    // equal what the editor reports back, causing an infinite reconciliation
    // loop (this is the exact bug survey-platform's sanitizer hit and fixed).
    data.attrValue = declarations.length ? declarations.join('; ') + ';' : ''
  })
}

/**
 * Sanitizes rich-text HTML authored in survey-platform's Quill editor for
 * question titles and directions, restricted to exactly the tags/attributes
 * that editor's toolbar can produce.
 */
export function sanitizeHtml(htmlString: string): string {
  ensureStyleHookRegistered()

  const sanitized = purify.sanitize(htmlString, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  })

  // Quill's contenteditable output encodes trailing/repeated spaces as
  // &nbsp; (U+00A0) to survive HTML whitespace-collapsing. That defeats
  // word-wrap on long words, since a non-breaking space is never a wrap
  // point, so turn them back into normal breakable spaces for display.
  return sanitized.replace(/\u00A0|&nbsp;/g, ' ')
}

/**
 * Returns true if the given string contains actual HTML element nodes (not
 * just text). Used to distinguish rich text authored via the Quill editor
 * from legacy plain-text titles/directions, which should keep rendering as
 * plain text rather than being parsed as HTML.
 */
export function isValidHtml(value: string): boolean {
  const doc = new DOMParser().parseFromString(value, 'text/html')
  return doc.body.children.length > 0
}

/**
 * Strips HTML tags and decodes entities, returning plain text. Used anywhere
 * a title/directions value needs to be plain text (analytics payloads, the
 * test-mode question list, etc.) rather than rendered HTML.
 */
export function stripHtml(htmlString: string): string {
  const doc = new DOMParser().parseFromString(htmlString, 'text/html')
  return doc.body.textContent || ''
}

/**
 * Escapes a plain-text string so it can be safely spliced into an
 * already-sanitized HTML string without being interpreted as markup.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
